import { useEffect, useMemo, useState } from 'react';
import { api, type MembershipPlanResp } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import MembershipPaymentModal, { toMembershipPaymentTarget } from '@/components/payment/MembershipPaymentModal';

const MembershipSection = () => {
  const [plans, setPlans] = useState<MembershipPlanResp[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePlan, setActivePlan] = useState<MembershipPlanResp | null>(null);
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const resp = await api.getMembershipPlans();
        setPlans(resp);
        if (resp[0]?.id) {
          setSelectedPlanId(String(resp.find((item) => item.isRecommend === 1)?.id || resp[0].id));
        }
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : '會員方案載入失敗');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedPlan = useMemo(
    () => plans.find((item) => String(item.id) === selectedPlanId) || plans[0] || null,
    [plans, selectedPlanId]
  );

  const features = useMemo(
    () =>
      (selectedPlan?.featureList || '')
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean),
    [selectedPlan]
  );

  return (
    <section className="relative overflow-hidden bg-[#F8F9FC] py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-gradient-to-b from-violet-100/60 to-transparent blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mb-14 text-center">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1.5">
            <i className="ri-vip-crown-line text-sm text-violet-600" />
            <span className="text-xs font-semibold uppercase tracking-wide text-violet-700">會員訂閱</span>
          </span>
          <h2 className="mb-3 text-3xl font-bold tracking-tight text-[#0A1628]">一次訂閱，解鎖全部學習資源</h2>
          <p className="mx-auto max-w-xl text-sm leading-relaxed text-slate-500">
            使用你的真實會員方案與支付流程，完成訂閱後會立即更新會員身份與有效期。
          </p>
          {user?.member ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700">
              <i className="ri-shield-check-line" />
              {user.memberPlanName || '會員'}有效至 {user.memberExpireTime || '待同步'}
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="rounded-[32px] border border-white/70 bg-white/80 p-10 text-center text-sm text-slate-400 shadow-sm">會員方案載入中...</div>
        ) : error ? (
          <div className="rounded-[32px] border border-rose-200 bg-rose-50 p-10 text-center text-sm text-rose-600">{error}</div>
        ) : !selectedPlan ? (
          <div className="rounded-[32px] border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">尚未配置會員方案</div>
        ) : (
          <div className="grid items-start gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="grid gap-4">
              {features.map((item, index) => (
                <div key={`${item}-${index}`} className="flex items-start gap-4 rounded-xl border border-slate-100 bg-white p-4 transition-colors hover:border-violet-200">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-violet-50">
                    <i className="ri-check-line text-lg text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0A1628]">{item}</p>
                    <p className="text-xs leading-relaxed text-slate-500">啟用後會直接同步到你的帳戶權益與課程會員價。</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="sticky top-24">
              <div className="overflow-hidden rounded-2xl bg-[#0A1628]">
                <div className="h-1.5 bg-gradient-to-r from-violet-500 via-indigo-500 to-violet-400" />
                <div className="p-8">
                  <div className="mb-8 flex items-center gap-2 rounded-xl bg-white/10 p-1.5">
                    {plans.map((plan) => (
                      <button
                        key={String(plan.id)}
                        onClick={() => setSelectedPlanId(String(plan.id))}
                        className={`relative flex-1 rounded-lg px-2 py-3 text-center transition-all ${
                          selectedPlanId === String(plan.id)
                            ? 'bg-violet-600 text-white'
                            : 'text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        {plan.badgeText ? (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-amber-400 px-2 py-0.5 text-[9px] font-bold whitespace-nowrap text-[#0A1628]">
                            {plan.badgeText}
                          </span>
                        ) : null}
                        <span className="text-xs font-semibold">{plan.planName}</span>
                        {plan.highlightText ? (
                          <div className={`mt-1 text-[9px] font-medium ${selectedPlanId === String(plan.id) ? 'text-violet-200' : 'text-green-400'}`}>
                            {plan.highlightText}
                          </div>
                        ) : null}
                      </button>
                    ))}
                  </div>

                  <div className="mb-6 text-center">
                    <div className="mb-1 flex items-end justify-center gap-1">
                      <span className="leading-none text-5xl font-bold tabular-nums text-white">${selectedPlan.planPrice}</span>
                      <span className="mb-1.5 text-sm text-slate-400">{selectedPlan.billingCycle}</span>
                    </div>
                    {Number(selectedPlan.originalPrice ?? 0) > Number(selectedPlan.planPrice ?? 0) ? (
                      <p className="text-xs text-slate-400">原價 ${selectedPlan.originalPrice}</p>
                    ) : null}
                  </div>

                  <div className="mb-6 border-t border-white/10" />

                  <div className="mb-7 flex flex-col gap-2.5">
                    {features.slice(0, 6).map((item) => (
                      <div key={item} className="flex items-center gap-2.5">
                        <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-green-500/20">
                          <i className="ri-check-line text-[10px] text-green-400" />
                        </div>
                        <span className="text-sm text-slate-300">{item}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setActivePlan(selectedPlan)}
                    className="w-full rounded-xl bg-violet-600 py-3.5 text-sm font-bold text-white transition-all hover:bg-violet-500"
                  >
                    {selectedPlan.buttonText || `立即訂閱 · $${selectedPlan.planPrice}${selectedPlan.billingCycle || ''}`}
                  </button>
                  <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-500">
                    付款成功後會立即更新帳戶會員身份，並同步課程會員價格。
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {activePlan ? (
        <MembershipPaymentModal
          target={toMembershipPaymentTarget(activePlan)}
          onClose={() => setActivePlan(null)}
          onSuccess={() => {
            void refreshUser();
            setActivePlan(null);
          }}
        />
      ) : null}
    </section>
  );
};

export default MembershipSection;
